import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { ComputedRef } from 'vue';
import { useAccountStore } from '~/stores/accountStore';
import { useEventCalendarStore } from '~/stores/eventCalendar';
import type { FullCalendarEvent, FullCalendarItem } from '~/types/fullCalendar';
import type { ITimelineItem, ITimelineRequestBody } from '~/types/types';
import { ETimelineFilterType, ETimelineIncludeType } from '~/types/enums';
import { isValidDate } from '~/utils/date';
import { useUrlParams } from '~/composables/urlParams';

const baseRequestBody: ITimelineRequestBody = {
    filter: {
        types: [ETimelineFilterType.CALENDAR_EVENT],
    },
    include: [ETimelineIncludeType.MR_CITY, ETimelineIncludeType.FILES, ETimelineIncludeType.IMAGES],
    includeExcludedTimelineGroupPosts: true,
};

export const useEventCalendarPreview = () => {
    const selectedItem = ref<ITimelineItem | null>(null);
    const selectedItems = ref<FullCalendarItem[]>([]);
    const selectedDate = ref<string | null>(null);
    const { state } =  storeToRefs(useAccountStore());

    const { locale } = useI18n();
    const eventCalendarStore = useEventCalendarStore();
    const { fetchEventCalendar, setRange } = eventCalendarStore;
    const { items: calendarItems, validFrom, validTo, postGroupIds } = storeToRefs(eventCalendarStore);

    const requestBody = computed(() => {
        const _requestBody = { ...baseRequestBody };
        _requestBody.filter.cityId = [state.value.appCityId];

        if (validFrom.value && validTo.value) {
            _requestBody.filter.validInRange = { from: validFrom.value, to: validTo.value };
        }

        if (postGroupIds.value.length) {
            _requestBody.filter.groupIds = postGroupIds.value;
        } else {
            delete _requestBody.filter.groupIds;
        }

        return _requestBody;
    });

    const onItemPreview = (postId: number) => {
        selectedItem.value = calendarItems.value.find(({ id }) => id === postId) || null;
    };

    const onItemShowMoreClick = (items: FullCalendarItem[], date: string) => {
        selectedItems.value = items;
        selectedDate.value = date;
    };

    const onCloseSelectedItems = () => {
        selectedItems.value = [];
        selectedDate.value = null;
    };

    const fetchEventCalendarByMonth = async (dateFrom: string) => {
        calendarItems.value = [];
        const dateTo = moment(dateFrom).endOf('month').format('YYYY-MM-DD 23:59:59');
        setRange(dateFrom, dateTo);
        calendarItems.value = await fetchEventCalendar(locale.value, requestBody.value);
    };

    const onMonthChange = async (event?: FullCalendarEvent[]) => {
        if (!event) {
            return;
        }
        const [data] = event;
        const dateFrom = moment()
            .startOf('month')
            .set('month', data.month - 1)
            .set('year', data.year)
            .format('YYYY-MM-DD 00:00:00');
        await fetchEventCalendarByMonth(dateFrom);
    };

    const items: ComputedRef<FullCalendarItem[]> = computed(() => calendarItems.value.map((item) => {
        const dateStart = moment(item.eventValidFrom).toDate();
        const dateEnd = item.eventValidTo ? moment(item.eventValidTo).toDate() : dateStart;
        return {
            customData: {
                id: item.id,
                title: item.title,
            },
            dates: {
                start: dateStart,
                end: dateEnd
            }
        };
    }));

    const { getParam } = useUrlParams();
    (()=> {
        const validFromParam = `${getParam('validFrom') ?? ''}`;
        if (isValidDate(validFromParam)) {
            return setRange(moment(validFromParam), moment(validFromParam).endOf('month'));
        }
        setRange(moment().startOf('month'), moment().endOf('month'));
    })();

    const initialMonth = moment(validFrom.value).month() + 1;
    const initialYear = moment(validFrom.value).year();

    return {
        items,
        initialMonth,
        initialYear,
        selectedItem,
        selectedItems,
        selectedDate,
        onMonthChange,
        onItemPreview,
        onItemShowMoreClick,
        onCloseSelectedItems,
        fetchEventCalendarByMonth
    };
};
